import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Beaker, Sparkles, Flame, Info } from "lucide-react";

export default function DemoModeCard() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/settings"); setSettings(data); } catch (e) { console.warn("DemoModeCard settings load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/settings", { demo_mode: !settings?.demo_mode });
      setSettings(data);
      window.dispatchEvent(new Event("nicu-settings-changed"));
      toast.success(data.demo_mode ? "Training mode ON" : "Training mode OFF");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const seed = async () => {
    if (!settings?.demo_mode) { toast.error("Enable training mode first"); return; }
    if (!window.confirm("Seed 6 realistic demo patients into vacant beds? All rows are flagged demo=true so you can purge them in one click.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/seed");
      toast.success(`Seeded ${data.created.length} demo patients`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed failed"); } finally { setBusy(false); }
  };

  const purge = async () => {
    if (!window.confirm("PURGE every row flagged demo=true across all collections? This cannot be undone.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo/purge");
      const total = Object.values(data.purged || {}).reduce((a, b) => a + b, 0);
      toast.success(`Purged ${total} demo rows`);
    } catch { toast.error("Purge failed"); } finally { setBusy(false); }
  };

  if (!settings) return null;
  const on = settings.demo_mode;

  return (
    <div className={`card-clinical p-5 flex items-start gap-4 ${on ? "border-l-4 border-l-amber-500" : ""}`} data-testid="demo-card">
      <div className={`h-11 w-11 rounded-xl grid place-items-center ${on ? "bg-warn" : "bg-muted text-muted-foreground"}`}>
        <Beaker size={20} />
      </div>
      <div className="flex-1">
        <div className="font-medium">Training / Demo mode</div>
        <div className="text-xs text-muted-foreground mt-1">
          When ON, an amber <b>TRAINING MODE</b> banner is shown ward-wide so no-one confuses practice patients with real ones. You can then seed 6 realistic demo babies into vacant beds and purge them in one tap.
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" className="rounded-full" onClick={toggle} disabled={busy} data-testid="demo-toggle">
            <Sparkles size={14} className="mr-2" /> {on ? "Turn OFF" : "Turn ON"}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={seed} disabled={busy || !on} data-testid="demo-seed">Seed 6 demo patients</Button>
          <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-500/40 hover:bg-red-500 hover:text-white" onClick={purge} disabled={busy} data-testid="demo-purge">
            <Flame size={14} className="mr-2" /> Purge demo data
          </Button>
        </div>
        {on && (
          <div className="mt-3 text-xs text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/25 rounded-md px-3 py-2 flex items-start gap-2">
            <Info size={12} className="mt-0.5 shrink-0" />
            Training mode is ON. The ward-wide banner will remind every user. Only rows marked demo=true will be created/purged.
          </div>
        )}
      </div>
    </div>
  );
}
